import { useState, type ComponentType, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { ApiError } from "../lib/apiFetch";
import type { PartnerLoginChallengeResponse } from "../types/api";
import { AuthCard, type AuthCardProps } from "./AuthCard";
import { usePortalApi } from "./usePortalApi";
import { usePortalAuthConfig } from "./usePortalAuthConfig";

/**
 * Portal login (`/login`), the password step of two-factor sign-in.
 *
 * Email and password POST to `<apiPrefix>/auth/login`, with the config's
 * `authExtra` merged in (the GPO portal's tenant `slug`), so the body is a
 * `PartnerLoginRequest` or a `GpoLoginRequest`. A correct password returns a
 * short-lived `challenge` and no session, so the screen routes to
 * `/verify-login` with it; that screen sends and checks the code. A 401 shows a
 * generic message so the response never reveals which field was wrong. A 429
 * shows the rate-limit copy, and a 403 (a disabled or phoneless account) shows
 * the server's message verbatim.
 *
 * The title and subtitle come from the config's `copy`; `loginHelp`, when set,
 * renders in the footer after the forgot-password link.
 */
export function Login({
  layout: Layout = AuthCard,
}: {
  /** The portal supplies presentation while the shared screen owns sign-in. */
  layout?: ComponentType<AuthCardProps>;
} = {}): ReactNode {
  const api = usePortalApi();
  const { apiPrefix, authExtra, copy, loginHelp } = usePortalAuthConfig();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // `title` says what happened; `body`, when present, says what to do next.
  const [error, setError] = useState<{ title: string; body?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const canSubmit = !busy && !!email.trim() && !!password;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const body: Record<string, string> = { ...authExtra, email: email.trim(), password };
    api<PartnerLoginChallengeResponse>(`${apiPrefix}/auth/login`, { method: "POST", body })
      .then((res) => {
        // The password passed and no session exists yet. Carry the non-secret
        // challenge and the `bypassSmsOtp` flag, which drives the verify
        // step's bypass banner, to the verify step. React Router v7's
        // `navigate` returns a promise that isn't awaited.
        void navigate("/verify-login", {
          state: { challenge: res.challenge, bypassSmsOtp: res.bypassSmsOtp },
        });
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError) {
          // A 429 is the server's rate limit. A 401 is a generic credential
          // failure; the backend never reveals which field was wrong. A 403 is
          // a disabled or phoneless account behind a correct password, and its
          // user-facing server message is shown verbatim.
          if (err.status === 429) {
            setError({
              title: "Too many attempts.",
              body: "Wait a few minutes, then try signing in again.",
            });
            return;
          }
          if (err.status === 401) {
            setError({
              title: "Invalid email or password.",
              body: "Check both and try again, or use “Forgot your password?” below to reset it.",
            });
            return;
          }
          if (err.status === 403) {
            setError({ title: err.detail });
            return;
          }
        }
        setError({
          title: "Something went wrong.",
          body: "Try again. If it keeps happening, use “Contact us” below.",
        });
      });
  };

  return (
    <Layout
      title={copy.loginTitle}
      subtitle={copy.loginSubtitle}
      footer={
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <Link
            to="/forgot-password"
            className="text-primary-text hover:text-ember-dark inline-flex min-h-11 items-center font-semibold"
          >
            Forgot your password?
          </Link>
          {loginHelp && (
            <>
              <span aria-hidden="true" className="text-haze text-body-lg">
                |
              </span>
              {loginHelp}
            </>
          )}
        </div>
      }
    >
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <Input
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <Alert tone="error" title={error.title}>
            {error.body}
          </Alert>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={!canSubmit}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </Layout>
  );
}
